// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import { TFunction } from 'i18next';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseTable } from '~/components/common';
import { ProductVariantFormModal } from '~/components/module/inventory';
// Others
import { IHeaderCellType } from '~/utils/interface/common';
import { EMPTY_VALUE } from '~/utils/constants/common';
import { IFormInventory, IItemVariant } from '~/utils/interface/inventory';
import { formatCurrencyUSD } from '~/utils/helper/common';
// Styles, images, icons
import styles from './ProductVariantInventoryForm.module.scss';
import { icons } from '~/assets';

type Props = {
  onRemoveVariant?: (id: string) => void;
  onRemoveFile?: (id: string) => void;
};

const cx = classNames.bind(styles);

const ProductVariantInventoryForm = (props: Props) => {
  //#region Destructuring Props
  const { onRemoveVariant, onRemoveFile } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);
  const { id: inventoryId } = useParams();

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<IFormInventory>();

  const { append: appendItems, remove: removeItems } = useFieldArray({
    control,
    name: 'variants',
  });

  const { variants: listVariants } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [dataVariant, setDataVariant] = useState<IItemVariant | null>();
  const [indexItem, setIndexItem] = useState<number>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    setIsShowModal(!isShowModal);
  };

  const handleAddItem = () => {
    setIsShowModal(true);
    setDataVariant(null);
  };

  const columnsItem = (t: TFunction): IHeaderCellType<IItemVariant>[] => {
    return [
      {
        id: 'name',
        label: t('inventory_form_title_product_variants_name'),
        render: (record: IItemVariant) => record.name || EMPTY_VALUE,
        minWidth: 100,
      },
      {
        id: 'quantity',
        label: t('inventory_form_title_product_variants_quantity'),
        render: (record: IItemVariant) => record.quantity,
        minWidth: 100,
      },
      {
        id: 'price',
        label: t('inventory_form_title_product_variants_price'),
        render: (record: IItemVariant) => <>{formatCurrencyUSD({ value: record.rentalPrice, showDecimal: true })}</>,
        minWidth: 100,
      },
      {
        id: 'action',
        label: t('inventory_form_title_product_variants_action'),
        minWidth: 100,
        align: 'center',
        render: (record: IItemVariant, index: number) => {
          return (
            <Stack direction={'row'} gap={1}>
              <IconButton
                className={cx('buttonIcon')}
                onClick={() => {
                  setDataVariant(record);
                  setIndexItem(index);
                  setIsShowModal(true);
                }}
              >
                <img
                  className={cx('iconEdit')}
                  src={icons.tableIconActionEdit}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                />
              </IconButton>

              {inventoryId && (
                <IconButton
                  className={cx('buttonIcon')}
                  onClick={() => {
                    removeItems(index);
                    onRemoveVariant?.(String(record?.id));
                  }}
                >
                  <img
                    className={cx('iconDelete')}
                    src={icons.commonIconTrash}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                  />
                </IconButton>
              )}
            </Stack>
          );
        },
      },
    ];
  };
  //#endregion Handle Function

  return (
    <Stack id='productVariantInventoryFormComponent' gap={4}>
      <Typography className={cx('header')}>{t('inventory_form_title_product_variants')}</Typography>

      <BaseTable columns={columnsItem(t)} dataSource={listVariants} />

      <Box>
        <Button
          type='button'
          variant='outlined'
          onClick={handleAddItem}
          startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
        >
          {t('inventory_form_add_on_title_add_item')}
        </Button>
      </Box>

      {isShowModal && (
        <ProductVariantFormModal
          dataVariant={dataVariant}
          indexItem={indexItem}
          onRemoveFile={onRemoveFile}
          onClose={handleCloseModal}
        />
      )}
    </Stack>
  );
};

export default ProductVariantInventoryForm;
